import * as babylon from "@babylonjs/core";

import * as schema from "@/shared/state/visualization/schema/v1/three-d";

import { DataServerManager } from "../DataServer/DataServerManager";
import { type ModelAssetLoader } from "../ModelLoader";

import * as components from "./components";
import type { MeasureState } from "./measure/measureSession";

/**
 * Plain `{x, y, z}` counterpart to Babylon's `Vector3`. Used in
 * serializable state where reaching into Babylon internals would couple
 * persistence and tests to runtime objects.
 */
export interface Vector3Plain {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

export interface BabylonState {
  entities: Record<schema.EntityID, Entity>;
  sceneConfig: SceneConfig;
  input: BabylonInput;
  readonly context: BabylonContext;
}

/**
 * App-controlled input state that systems write and Babylon consumes.
 * Plain-shaped so persistence and tests don't reach into Babylon
 * internals.
 */
export interface BabylonInput {
  /**
   * Summing accumulator for third-person camera input. DOM event
   * handlers (`handleOrbitInput`, `handlePanInput`, `handleZoomInput`)
   * write per-event deltas at the same sensitivity calibrations the
   * codebase ships today; `updateCamera`'s third-person branch reads
   * the accumulator each frame, applies it to `babylonCamera.{alpha,
   * beta, radius}` and `camera.focusPoint`, then zeroes every consumed
   * field. The accumulator decouples pointer-event rate from frame
   * rate — every event contributes exactly once per frame to camera
   * state. Orbit input continues to flow through Babylon's
   * `ArcRotateCameraPointersInput`, so these fields stay at zero in
   * orbit mode.
   */
  cameraDeltas: CameraDeltas;
  /**
   * The pointer tool's mode and cursor state. `BabylonManager` always
   * builds one; optional only so hand-built states (tests, older fixtures)
   * stay valid. Consumers treat absence as "no tool".
   */
  tool?: ToolInput;
}

/**
 * Whether the pointer tool is active. `"none"` is plain camera navigation;
 * `"measure"` reads out the coordinates under the hovered cursor and pins
 * two picked points into a distance measurement.
 */
export type ToolMode = "none" | "measure";

/**
 * State for the pointer tool: whether it is live, the cursor position,
 * measurements, and what has already been reported to React.
 *
 * {@link pointer} is written by `attachToolPointerInput` at pointer-event
 * rate and consumed once per frame by the post-render systems
 * (`reportToolHover`, `reportMeasureOverlay`) — the same
 * event-rate/frame-rate decoupling `cameraDeltas` uses. {@link pendingPins}
 * is the exception: a click is picked where it happens, against the frame
 * the user clicked on.
 */
export interface ToolInput {
  mode: ToolMode;
  /**
   * Latest cursor position over the canvas, in canvas-relative CSS px.
   * Absent when the pointer is off the canvas. Tracked in both modes, so
   * turning the tool on mid-hover answers immediately.
   */
  pointer?: { x: number; y: number };
  /**
   * What `onToolHover` was last told, so an unchanged hover is reported
   * once, not every frame. `reportToolHover` treats absent and `null`
   * alike as "no hover outstanding".
   */
  reportedHover?: ToolHover | null;
  /**
   * Measure-mode clicks waiting to become pins, already resolved to
   * data-space points, in click order. The pick runs in the pointer
   * handler, against the last rendered frame — the frame the user was
   * looking at when they clicked — rather than a later frame whose camera
   * may have moved. Clicks that resolve to the background are dropped.
   * A list rather than one slot because a stalled frame can swallow two
   * clicks in one gap; `executeMeasurePin` drains it each frame.
   */
  pendingPins?: Vector3Plain[];
  /**
   * The measure tool's session and completed measurements, in data space.
   * Survives the tool being turned off: that stops adding measurements
   * without destroying them. Absent until the first pin.
   */
  measure?: MeasureState;
  /**
   * What `onMeasureOverlay` was last told, so a still camera over still
   * measurements reports once per change, not per frame.
   * `reportMeasureOverlay` compares field for field: projected positions
   * within half a pixel count as unchanged, the cursor is compared
   * exactly. As with {@link reportedHover}, absent and `null` both mean
   * "nothing reported".
   */
  reportedMeasureOverlay?: MeasureOverlayProjection | null;
  /**
   * The inputs the last hover pick answered for, letting a frame that
   * changed none of them skip the pick. Written and read only by
   * `reportToolHover`, which documents the layout.
   */
  hoverPickInputs?: Float64Array;
}

/**
 * Screen-space projection of the measure tool's state, for the React
 * overlay: where the in-progress anchor sits (React draws the rubberband
 * line from it to the cursor) and where each completed measurement's label
 * belongs. Recomputed per frame because the projections move with the
 * camera, but reported only on change.
 *
 * Named for the projection so it does not collide with the
 * `MeasureOverlay` component that consumes it.
 */
export interface MeasureOverlayProjection {
  /** The in-progress anchor, projected. Absent when nothing is anchored. */
  anchor?: MeasureOverlayPoint;
  /**
   * The cursor the rubberband line runs to. Present only while an anchor
   * exists, the measure tool is active, and the pointer is on the canvas.
   * Canvas-relative CSS px, like every screen coordinate here.
   */
  cursor?: { screenX: number; screenY: number };
  /** One label per completed measurement, at the projected midpoint. */
  labels: Array<{
    id: number;
    lengthMeters: number;
    point: MeasureOverlayPoint;
  }>;
}

/** A projected overlay position, canvas-relative CSS px. */
export interface MeasureOverlayPoint {
  screenX: number;
  screenY: number;
  /** False when the point is behind the camera — draw nothing there. */
  visible: boolean;
}

/**
 * The Babylon objects drawing one completed measurement. Defined here
 * rather than beside `renderMeasures`, their one writer, because
 * `SceneConfig` stores them and systems import state, never the reverse.
 */
export interface MeasureVisual {
  /** The line as `renderMeasures` draws it twice: depth-tested… */
  solid: babylon.LinesMesh;
  /** …and a faint copy on top of everything, so occluded spans stay legible. */
  xray: babylon.LinesMesh;
  startDot: babylon.Mesh;
  endDot: babylon.Mesh;
}

/**
 * What the measure tool found under the cursor, for the React overlay. It
 * becomes two things at once: the coordinate readout, and the snap marker
 * showing which point a click would pin.
 */
export interface ToolHover {
  /** The picked location in data space (see `pickScenePoint`). */
  dataPoint: Vector3Plain;
  /** Cursor position the pick answers, canvas-relative CSS px. */
  screenX: number;
  screenY: number;
  /**
   * Where {@link dataPoint} itself lands on screen, canvas-relative CSS
   * px. Within a pixel of the cursor on a surface, but up to
   * `PICK_RADIUS_PX` away on a point cloud, where the pick snaps to a
   * vertex — the offset the snap marker shows.
   */
  pickScreenX: number;
  pickScreenY: number;
  /**
   * Log time of the trajectory sample the pick landed on, in epoch
   * nanoseconds. Present only for a path-sample hit; the coordinate
   * readout shows it, and nothing else reads it.
   */
  sampleTime?: bigint;
}

/**
 * Per-frame input deltas summed by DOM handlers and consumed by
 * `updateCamera`. Each field maps to one third-person gesture.
 */
export interface CameraDeltas {
  /** Orbit drag, alpha axis (horizontal). Radians. */
  alpha: number;
  /** Orbit drag, beta axis (vertical). Radians. */
  beta: number;
  /** Shift+drag pan along world X. Data-space units. */
  panX: number;
  /** Shift+drag pan along world Z. Data-space units. */
  panZ: number;
  /** Wheel scroll. Sign matches Babylon's radius convention. */
  zoom: number;
  /** Shift+wheel elevation. Adds to `camera.focusPoint.y`. Data-space units. */
  elevateY: number;
}

/**
 * Zero value for `CameraDeltas`. Used for first-frame init in
 * `BabylonManager.initState` and for the mode-flip reset in
 * `updateSceneConfig` (see those sites for the rationale). Spread into
 * a fresh object at each use site — `Readonly` enforces this at the
 * type level, but callers still need a mutable copy to write to.
 */
export const ZERO_CAMERA_DELTAS: Readonly<CameraDeltas> = {
  alpha: 0,
  beta: 0,
  panX: 0,
  panZ: 0,
  zoom: 0,
  elevateY: 0,
};

export interface BabylonContext {
  readonly systemSchedules: Record<string, SystemSchedule>;
  readonly dataServer: DataServerManager;
  readonly modelLoader: ModelAssetLoader;
  readonly engine: babylon.Engine;
  readonly scene: babylon.Scene;
  readonly callbacks: Callbacks;
  readonly canvas: HTMLCanvasElement;
}

export interface SystemSchedule {
  dispose: () => void;
}

export interface Callbacks {
  onLoadingStatusChanged: (isLoading: boolean) => void;
  /**
   * Reports a model that could not be loaded — a missing external buffer, an
   * unparseable document, an expired URL.
   *
   * Deliberately separate from `onLoadingStatusChanged`, which is a one-shot
   * signal fired once when the scene becomes ready. Routing model loads
   * through it would flash the whole-panel spinner every time a model is
   * added, and could never report *which* model failed.
   */
  onModelLoadFailed: (modelId: string, error: Error) => void;
  /**
   * Reports what the measure tool is over — a hover to show, or `null`
   * when the cursor left the data or the tool was turned off. Fired from
   * the render loop, only on change; see `reportToolHover`. Optional
   * because the ingest preview builds a manager with no tool UI.
   */
  onToolHover?: (hover: ToolHover | null) => void;
  /**
   * Reports the measure tool's screen-space overlay — the projected
   * anchor and measurement labels — or `null` when there is nothing to
   * draw. Fired from the render loop, only on change; see
   * `reportMeasureOverlay`. Optional for the same reason as
   * {@link onToolHover}.
   */
  onMeasureOverlay?: (overlay: MeasureOverlayProjection | null) => void;
}

export interface BabylonTheme {
  backgroundColor: babylon.Color4;
}

export type Entity = {
  id: schema.EntityID;
} & Partial<{
  position: components.Position;
  rotation: components.Rotation;
  /**
   * A joint value something is driving right now: overriding what `rotation`
   * declares if the joint turns, adding to what `position` declares if it
   * slides. Absent means the entity is exactly where its specs put it.
   */
  jointPose: components.JointPose;
  globalPosition: components.GlobalPosition;
  mesh: components.Mesh;
  path: components.Path;
  vectorField: components.VectorField;
}>;

/**
 * A special singleton entity whose components are only needed once.
 */
export type SceneConfig = Partial<{
  grid: components.singletons.Grid;
  camera: components.singletons.Camera;
  /**
   * The Babylon objects drawing the measure tool's state, keyed by
   * measurement id, plus the in-progress anchor's dot. Owned and
   * reconciled by `renderMeasures` each frame; nothing else writes it.
   */
  measureVisuals: {
    byId: Map<number, MeasureVisual>;
    anchorDot?: babylon.Mesh;
  };
  /**
   * Entity ids ordered so every entity follows its parent.
   *
   * Cached here rather than recomputed per frame because it only changes when
   * the entity set or a `GlobalPosition.spec` does — `updateGlobalPosition` is
   * the single writer. Lives on the scene config, next to `entityGeneration`,
   * because it describes the whole entity set rather than any one entity.
   */
  entityOrder: schema.EntityID[];
  time: bigint;
  /**
   * Bumped whenever the bounded content of the scene changes:
   * `updateEntities` for entity adds/removes/mesh rescales,
   * `reloadStalePaths` when a new path mesh is built, and
   * `reloadStaleMeshes` when a `model_node`'s geometry lands. Read by
   * `computeThirdPersonZoom`'s cache to decide whether to recompute
   * the scene-union envelope; a stable generation means the cached
   * result is still valid.
   *
   * Named for entities because that was its only source originally; it is
   * really a scene-content generation.
   */
  entityGeneration: number;
  /**
   * Cached result of `computeThirdPersonZoom`, valid for the
   * `entityGeneration` it was computed against. Mismatched generation
   * triggers a recompute on the next read.
   */
  thirdPersonZoomCache: {
    value: { min: number; max: number };
    generation: number;
  };
  /**
   * Cached result of `constrainCamera`'s scene-derived envelope — the zoom
   * limits and the draw distance — valid for the `entityGeneration` and camera
   * spec it was computed against.
   *
   * Cached rather than recomputed per frame because it describes the *scale of
   * the scene's content*, which changes when the content does and not while it
   * moves. That distinction is invisible for a rigid mesh and decisive for a
   * `model`: a model's extent is the union of a posed hierarchy, so measuring it
   * every frame makes the envelope — and the fog, the depth planes and the grid
   * that derive from it — oscillate at playback rate. See `constrainCamera`.
   */
  cameraEnvelope: {
    value: { zoom: { min: number; max: number }; maxDrawDistance: number };
    generation: number;
    spec: schema.Camera;
  };
}>;
